"use client";

import  { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom'; 
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  GeoJSON,
  useMap,
  useMapEvents,
  ScaleControl,
  FeatureGroup,
  Tooltip as LeafletTooltip,
} from "react-leaflet";

import L from "leaflet";

import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
// npm install jszip @tmcw/togeojson
import JSZip from "jszip";
import { kml as kmlToGeoJson } from "@tmcw/togeojson";

import {
  Box,
  IconButton,
  TextField,
  Button,
  Paper,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Switch,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LayersIcon from "@mui/icons-material/Layers";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CropFreeIcon from "@mui/icons-material/CropFree";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExploreIcon from "@mui/icons-material/Explore";
import LabelIcon from "@mui/icons-material/Label";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PentagonIcon from "@mui/icons-material/Pentagon";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";

// ======================================================
// LEAFLET MARKER FIX
// ======================================================

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// A distinct icon for user-dropped pins so they're easy to tell apart from
// search markers / KML markers.
const pinIcon = new L.Icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  className: "lims-pin-marker",
});

// ======================================================
// MAP CONFIGURATION
// ======================================================

const PAKISTAN_CENTER = [30.3753, 69.3451];

const SATELLITE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

const STREET_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

// Esri's boundary/place reference layer — good for country / province /
// district level labels.
const ADMIN_LABELS_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";

// CARTO's "labels only" layer is built from OpenStreetMap place data and
// renders many more granular labels as you zoom in — towns, tehsils and
// villages included, wherever OSM has that data mapped.
const PLACE_LABELS_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png";

// It must be prefixed NEXT_PUBLIC_ to be readable in the browser bundle.
const OPENWEATHER_API_KEY =
  "790e1ca3721b9c134e8b2a64f8552718" || "ec5500d79efcd3e3da3b76f40e0e1092";

const KML_LAYER_COLORS = [
  "#e53935",
  "#8e24aa",
  "#3949ab",
  "#00897b",
  "#f4511e",
  "#6d4c41",
];

// ======================================================
// AREA CALCULATION
// ======================================================

function getLayerArea(layer) {
  let squareMeters = 0;

  if (layer instanceof L.Circle) {
    const radius = layer.getRadius();
    squareMeters = Math.PI * radius * radius;
  } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
    let latLngs = layer.getLatLngs();

    while (Array.isArray(latLngs) && Array.isArray(latLngs[0])) {
      latLngs = latLngs[0];
    }

    if (Array.isArray(latLngs) && latLngs.length >= 3) {
      const earthRadius = 6378137;
      let area = 0;

      for (let i = 0; i < latLngs.length; i++) {
        const current = latLngs[i];
        const next = latLngs[(i + 1) % latLngs.length];

        const lat1 = (current.lat * Math.PI) / 180;
        const lat2 = (next.lat * Math.PI) / 180;
        const lng1 = (current.lng * Math.PI) / 180;
        const lng2 = (next.lng * Math.PI) / 180;

        area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
      }

      squareMeters = (Math.abs(area) * (earthRadius * earthRadius)) / 2;
    }
  }

  return {
    squareMeters,
    hectares: squareMeters / 10000,
    acres: squareMeters / 4046.8564224,
  };
}

// ======================================================
// SHAPE CENTER (used to fetch weather for whatever the
// user just drew / dropped / uploaded)
// ======================================================

function getShapeCenter(layer) {
  if (layer instanceof L.Circle || layer instanceof L.CircleMarker) {
    const center = layer.getLatLng();
    return { latitude: center.lat, longitude: center.lng };
  }

  if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
    const center = layer.getBounds().getCenter();
    return { latitude: center.lat, longitude: center.lng };
  }

  if (typeof layer.getLatLng === "function") {
    const center = layer.getLatLng();
    return { latitude: center.lat, longitude: center.lng };
  }

  if (typeof layer.getBounds === "function") {
    const center = layer.getBounds().getCenter();
    return { latitude: center.lat, longitude: center.lng };
  }

  return null;
}

function getGeoJsonCenter(geoJson) {
  try {
    const bounds = L.geoJSON(geoJson).getBounds();
    if (!bounds.isValid()) return null;
    const center = bounds.getCenter();
    return { latitude: center.lat, longitude: center.lng };
  } catch (error) {
    console.error("Unable to compute KML/KMZ center:", error);
    return null;
  }
}

// ======================================================
// WEATHER HELPERS
// ======================================================

function kToC(kelvin) {
  return typeof kelvin === "number" ? +(kelvin - 273.15).toFixed(1) : null;
}

const COMPASS_DIRECTIONS = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
];

function degToCompass(deg) {
  const index = Math.round((deg % 360) / 22.5) % 16;
  return COMPASS_DIRECTIONS[index];
}

// ======================================================
// ETo CALCULATION (Hargreaves-Samani)
// ======================================================

function getDayOfYear(date) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function computeETo(latDeg, tmaxC, tminC, date) {
  if (tmaxC == null || tminC == null) return null;

  const latRad = (latDeg * Math.PI) / 180;
  const J = getDayOfYear(date);
  const dr = 1 + 0.033 * Math.cos(((2 * Math.PI) / 365) * J);
  const delta =
    (23.45 * Math.sin(((2 * Math.PI) / 365) * (J - 81)) * Math.PI) / 180;
  const ws = Math.acos(-Math.tan(latRad) * Math.tan(delta));
  // Ra in MJ/m²/day
  const Gsc = 0.082;
  const Ra_MJ =
    ((24 * 60) / Math.PI) *
    Gsc *
    dr *
    (ws * Math.sin(latRad) * Math.sin(delta) +
      Math.cos(latRad) * Math.cos(delta) * Math.sin(ws));
  // Convert to mm/day equivalent (1 MJ/m²/day ≈ 0.408 mm/day)
  const Ra_mm = Ra_MJ * 0.408;

  const tavg = (tmaxC + tminC) / 2;
  // Hargreaves equation
  const ETo = 0.0023 * Math.sqrt(tmaxC - tminC) * (tavg + 17.8) * Ra_mm;
  return Math.round(ETo * 10) / 10; // one decimal
}

// ======================================================
// MAP CONTROLLER
// ======================================================

function MapController({ searchLocation, userLocation }) {
  const map = useMap();

  useEffect(() => {
    if (!searchLocation) return;
    map.flyTo([searchLocation.latitude, searchLocation.longitude], 15, {
      duration: 1.5,
    });
  }, [searchLocation, map]);

  useEffect(() => {
    if (!userLocation) return;
    map.flyTo([userLocation.latitude, userLocation.longitude], 16, {
      duration: 1.5,
    });
  }, [userLocation, map]);

  useEffect(() => {
    const handler = (event) => {
      if (!event.detail) return;
      map.flyTo(
        [event.detail.latitude, event.detail.longitude],
        event.detail.zoom || 5,
        { duration: 1.5 },
      );
    };

    window.addEventListener("move-map", handler);
    return () => window.removeEventListener("move-map", handler);
  }, [map]);

  // Fits the map to the bounds of a newly uploaded KML/KMZ file.
  useEffect(() => {
    const handler = (event) => {
      const geoJson = event.detail?.geoJson;
      if (!geoJson) return;

      try {
        const tempLayer = L.geoJSON(geoJson);
        const bounds = tempLayer.getBounds();

        if (bounds.isValid()) {
          map.flyToBounds(bounds, { padding: [40, 40], duration: 1.2 });
        }
      } catch (error) {
        console.error("Unable to fit KML bounds:", error);
      }
    };

    window.addEventListener("lims-fit-kml", handler);
    return () => window.removeEventListener("lims-fit-kml", handler);
  }, [map]);

  return null;
}

// ======================================================
// MAP CLICK HANDLER
// ======================================================

function MapClickHandler({
  labelMode,
  setLabels,
  setLabelMode,
  pinMode,
  setPins,
  setPinMode,
  setClickedPosition,
  onPinPlaced,
}) {
  useMapEvents({
    click(event) {
      const { lat, lng } = event.latlng;

      // Always show clicked coordinates
      setClickedPosition({ latitude: lat, longitude: lng });

      // Drop a pin
      if (pinMode) {
        setPins((previous) => [
          ...previous,
          { id: Date.now(), latitude: lat, longitude: lng },
        ]);
        setPinMode(false);

        if (onPinPlaced) {
          onPinPlaced({ latitude: lat, longitude: lng });
        }

        return;
      }

      // Add a text label
      if (!labelMode) return;

      const text = window.prompt("Enter label name:");

      if (!text || !text.trim()) return;

      setLabels((previous) => [
        ...previous,
        { id: Date.now(), latitude: lat, longitude: lng, text: text.trim() },
      ]);

      setLabelMode(false);
    },
  });

  return null;
}

// ======================================================
// DRAWING CONTROLLER
// ======================================================

function MapInteractionController({
  featureGroupRef,
  setSelectedArea,
  onShapeCreated,
}) {
  const map = useMap();
  const activeDrawerRef = useRef(null);

  const stopDrawing = () => {
    if (activeDrawerRef.current) {
      try {
        activeDrawerRef.current.disable();
      } catch (error) {
        console.error("Unable to stop drawing:", error);
      }
      activeDrawerRef.current = null;
    }
  };

  const startDrawing = (type) => {
    if (!L.Draw) {
      console.error(
        "leaflet-draw did not initialize (L.Draw is undefined). " +
          'Check that "leaflet-draw" is imported as JS, not just its CSS.',
      );
      return;
    }

    stopDrawing();

    let drawer = null;

    if (type === "polygon") {
      drawer = new L.Draw.Polygon(map, {
        allowIntersection: false,
        showArea: true,
        repeatMode: false,
        shapeOptions: {
          color: "#1976d2",
          weight: 3,
          fillColor: "#2196f3",
          fillOpacity: 0.25,
        },
      });
    }

    if (type === "rectangle") {
      drawer = new L.Draw.Rectangle(map, {
        shapeOptions: {
          color: "#ff9800",
          weight: 3,
          fillColor: "#ff9800",
          fillOpacity: 0.2,
        },
      });
    }

    if (type === "circle") {
      drawer = new L.Draw.Circle(map, {
        shapeOptions: {
          color: "#9c27b0",
          weight: 3,
          fillColor: "#9c27b0",
          fillOpacity: 0.2,
        },
      });
    }

    if (!drawer) return;

    activeDrawerRef.current = drawer;
    drawer.enable();
  };

  useEffect(() => {
    // Guard: if leaflet-draw hasn't attached itself to L yet (e.g. during
    // a fast-refresh edge case), skip wiring this up instead of throwing.
    if (!L.Draw || !L.Draw.Event) {
      console.error(
        "leaflet-draw did not initialize (L.Draw is undefined). " +
          'Check that "leaflet-draw" is imported as JS, not just its CSS.',
      );
      return;
    }

    const handleCreated = (event) => {
      const layer = event.layer;
      const layerType = event.layerType;

      if (featureGroupRef.current) {
        featureGroupRef.current.addLayer(layer);
      }

      const area = getLayerArea(layer);

      setSelectedArea({ type: layerType, ...area });

      if (onShapeCreated) {
        const center = getShapeCenter(layer);
        if (center) {
          onShapeCreated(layerType, center);
        }
      }

      activeDrawerRef.current = null;
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    return () => map.off(L.Draw.Event.CREATED, handleCreated);
  }, [map, featureGroupRef, setSelectedArea, onShapeCreated]);

  useEffect(() => {
    const polygonHandler = () => startDrawing("polygon");
    const rectangleHandler = () => startDrawing("rectangle");
    const circleHandler = () => startDrawing("circle");
    const zoomInHandler = () => map.zoomIn();
    const zoomOutHandler = () => map.zoomOut();
    const resetHandler = () =>
      map.setView(PAKISTAN_CENTER, 5, { animate: true });

    window.addEventListener("lims-draw-polygon", polygonHandler);
    window.addEventListener("lims-draw-rectangle", rectangleHandler);
    window.addEventListener("lims-draw-circle", circleHandler);
    window.addEventListener("lims-zoom-in", zoomInHandler);
    window.addEventListener("lims-zoom-out", zoomOutHandler);
    window.addEventListener("lims-reset-map", resetHandler);

    return () => {
      stopDrawing();
      window.removeEventListener("lims-draw-polygon", polygonHandler);
      window.removeEventListener("lims-draw-rectangle", rectangleHandler);
      window.removeEventListener("lims-draw-circle", circleHandler);
      window.removeEventListener("lims-zoom-in", zoomInHandler);
      window.removeEventListener("lims-zoom-out", zoomOutHandler);
      window.removeEventListener("lims-reset-map", resetHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

// ======================================================
// KML FEATURE POPUP CONTENT (built with DOM APIs, not
// innerHTML, so uploaded file contents can never inject
// markup into the page)
// ======================================================

function buildKmlPopupContent(feature, fallbackName) {
  const name = feature?.properties?.name || fallbackName;
  const description = feature?.properties?.description || "";

  const container = document.createElement("div");
  container.style.minWidth = "150px";

  const title = document.createElement("div");
  title.style.fontWeight = "600";
  title.style.fontSize = "13px";
  title.textContent = name;
  container.appendChild(title);

  if (description) {
    const desc = document.createElement("div");
    desc.style.fontSize = "12px";
    desc.style.color = "#666";
    desc.style.marginTop = "4px";
    desc.textContent = description;
    container.appendChild(desc);
  }

  return container;
}

// ======================================================
// MAIN COMPONENT
// ======================================================

export default function MapScreen() {
  // ----------------------------------------------------
  // MAP
  // ----------------------------------------------------

  const [mapType, setMapType] = useState("satellite");
  const [showAdminLabels, setShowAdminLabels] = useState(true);
  const [showPlaceLabels, setShowPlaceLabels] = useState(true);

  // ----------------------------------------------------
  // SEARCH
  // ----------------------------------------------------

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null);
  const [searchMarker, setSearchMarker] = useState(null);

  // ----------------------------------------------------
  // LOCATION
  // ----------------------------------------------------

  const [userLocation, setUserLocation] = useState(null);

  // ----------------------------------------------------
  // MAP CLICK
  // ----------------------------------------------------

  const [clickedPosition, setClickedPosition] = useState(null);

  // ----------------------------------------------------
  // DRAWING
  // ----------------------------------------------------

  const [showLayers, setShowLayers] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const featureGroupRef = useRef(null);

  // ----------------------------------------------------
  // CUSTOM LABELS
  // ----------------------------------------------------

  const [labels, setLabels] = useState([]);
  const [labelMode, setLabelMode] = useState(false);

  // ----------------------------------------------------
  // PINS
  // ----------------------------------------------------

  const [pins, setPins] = useState([]);
  const [pinMode, setPinMode] = useState(false);

  // ----------------------------------------------------
  // KML / KMZ
  // ----------------------------------------------------

  const [kmlLayers, setKmlLayers] = useState([]); // { id, name, geoJson, visible, color }
  const [kmlError, setKmlError] = useState("");
  const [kmlLoading, setKmlLoading] = useState(false);
  const fileInputRef = useRef(null);

  // ----------------------------------------------------
  // WEATHER
  // ----------------------------------------------------

  const [weatherDays, setWeatherDays] = useState(null); // array of forecast days
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState("");
  const [weatherSource, setWeatherSource] = useState(null); // { label, icon, latitude, longitude }
  const [showWeatherPanel, setShowWeatherPanel] = useState(false);

  // ====================================================
  // SEARCH SUGGESTIONS
  // ====================================================
    
      const navigate = useNavigate(); 
const handleCalculateCropWater = () => {
  navigate('/crop-water-requriments');
};
  useEffect(() => {
    if (!search.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoadingSuggestions(true);

        const response = await fetch(
          "https://nominatim.openstreetmap.org/search?" +
            `q=${encodeURIComponent(search)}` +
            "&format=json" +
            "&addressdetails=1" +
            "&limit=6",
        );

        const data = await response.json();

        setSuggestions(Array.isArray(data) ? data : []);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Suggestion error:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  // ====================================================
  // SELECT SEARCH SUGGESTION
  // ====================================================

  const selectSuggestion = (result) => {
    const location = {
      latitude: Number(result.lat),
      longitude: Number(result.lon),
      name: result.display_name,
    };

    setSearch(result.display_name);
    setSearchLocation(location);
    setSearchMarker(location);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // ====================================================
  // SEARCH BUTTON
  // ====================================================

  const handleSearch = async () => {
    if (!search.trim()) return;

    try {
      setLoadingSearch(true);

      const response = await fetch(
        "https://nominatim.openstreetmap.org/search?" +
          `q=${encodeURIComponent(search)}` +
          "&format=json" +
          "&addressdetails=1" +
          "&limit=1",
      );

      const data = await response.json();

      if (!data || data.length === 0) {
        alert("Location not found.");
        return;
      }

      selectSuggestion(data[0]);
    } catch (error) {
      console.error("Search error:", error);
      alert("Unable to search this location.");
    } finally {
      setLoadingSearch(false);
    }
  };

  // ====================================================
  // CURRENT LOCATION
  // ====================================================

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.error(error);
        alert("Unable to get your current location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // ====================================================
  // FULLSCREEN
  // ====================================================

  const toggleFullscreen = () => {
    const element = document.getElementById("lims-map-container");

    if (!document.fullscreenElement) {
      element?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // ====================================================
  // RESET MAP
  // ====================================================

  const resetMap = () => {
    setSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchMarker(null);
    setSearchLocation(null);
    setSelectedArea(null);
    setLabelMode(false);
    setPinMode(false);
    setShowWeatherPanel(false);
    setWeatherDays(null);
    setWeatherError("");
    setWeatherSource(null);

    window.dispatchEvent(new CustomEvent("lims-reset-map"));
  };

  // ====================================================
  // WEATHER
  // ====================================================
  // Triggered whenever the user drops a pin, finishes drawing a
  // polygon / rectangle / circle, or uploads a KML/KMZ file — always
  // fetches a 10-day forecast for the shape's center point.

  const fetchWeatherForLocation = async ({
    latitude,
    longitude,
    label,
    icon,
  }) => {
    setShowWeatherPanel(true);
    setWeatherSource({ latitude, longitude, label, icon });
    setWeatherLoading(true);
    setWeatherError("");

    if (!OPENWEATHER_API_KEY) {
      setWeatherLoading(false);
      setWeatherError(
        "Missing OpenWeatherMap API key. Set NEXT_PUBLIC_OPENWEATHER_API_KEY in your .env file.",
      );
      setWeatherDays(null);
      return;
    }

    try {
      // Request only 10 days – no pagination
      const response = await fetch(
        `https://api.openweathermap.org/data/4.0/onecall/timeline/1day?lat=${latitude}&lon=${longitude}&cnt=10&appid=${OPENWEATHER_API_KEY}`,
      );

      if (!response.ok) {
        throw new Error(
          `Weather API error: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      // `data.data` contains the daily forecast array
      const forecast = data.data || [];

      const days = forecast.map((day, index) => {
        const tempMax = kToC(day.temp?.max);
        const tempMin = kToC(day.temp?.min);
        const tempDay = kToC(day.temp?.day);
        const tempNight = kToC(day.temp?.night);
        const feelsLike = kToC(day.feels_like?.day);
        const dateObj = new Date(day.dt * 1000);
        const eto = computeETo(latitude, tempMax, tempMin, dateObj);

        return {
          day: index + 1,
          date: dateObj,
          tempMax,
          tempMin,
          tempDay,
          tempNight,
          feelsLike,
          humidity: day.humidity,
          pressure: day.pressure,
          windSpeed: day.wind_speed,
          windDeg: day.wind_deg,
          windCompass: degToCompass(day.wind_deg),
          condition: day.weather?.[0]?.main || "N/A",
          description: day.weather?.[0]?.description || "N/A",
          icon: day.weather?.[0]?.icon,
          clouds: day.clouds,
          rain: day.rain ?? 0,
          pop: day.pop,
          popPercent: ((day.pop || 0) * 100).toFixed(0),
          // Alerts
          heatAlert:
            tempMax >= 40 ? "Heatwave" : tempMax >= 35 ? "High Temp" : null,
          frostAlert: tempMin <= 2 ? "Frost" : null,
          rainAlert:
            (day.rain ?? 0) >= 60
              ? "Heavy Rain"
              : (day.rain ?? 0) >= 30
                ? "Rain Expected"
                : null,
          windAlert:
            day.wind_speed >= 12
              ? "Strong Wind"
              : day.wind_speed >= 8
                ? "Windy"
                : null,
          sunrise: day.sunrise
            ? new Date(day.sunrise * 1000).toLocaleTimeString("en-US", {
                hour12: false,
              })
            : null,
          sunset: day.sunset
            ? new Date(day.sunset * 1000).toLocaleTimeString("en-US", {
                hour12: false,
              })
            : null,
          // ETo
          eto,
        };
      });

      setWeatherDays(days);
    } catch (error) {
      console.error("Weather fetch error:", error);
      setWeatherError(error.message || "Unable to fetch weather data.");
      setWeatherDays(null);
    } finally {
      setWeatherLoading(false);
    }
  };

  const handlePinPlaced = (location) => {
    fetchWeatherForLocation({ ...location, label: "Dropped Pin", icon: "pin" });
  };

  const handleShapeCreated = (shapeType, center) => {
    const labels = {
      polygon: "Polygon Area",
      rectangle: "Rectangle Area",
      circle: "Circle Area",
    };

    fetchWeatherForLocation({
      ...center,
      label: labels[shapeType] || "Drawn Area",
      icon: shapeType,
    });
  };

  const closeWeatherPanel = () => {
    setShowWeatherPanel(false);
  };

  // ====================================================
  // CUSTOM LABEL / PIN HELPERS
  // ====================================================

  const deleteLabel = (id) => {
    setLabels((previous) => previous.filter((label) => label.id !== id));
  };

  const deletePin = (id) => {
    setPins((previous) => previous.filter((pin) => pin.id !== id));
  };

  const activateDrawing = (type) => {
    setLabelMode(false);
    setPinMode(false);
    window.dispatchEvent(new CustomEvent(`lims-draw-${type}`));
  };

  const togglePinMode = () => {
    setLabelMode(false);
    setPinMode((previous) => !previous);
  };

  const toggleLabelMode = () => {
    setPinMode(false);
    setLabelMode((previous) => !previous);
  };

  // ====================================================
  // KML / KMZ UPLOAD
  // ====================================================

  const handleKmlUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isKmz = lowerName.endsWith(".kmz");
    const isKml = lowerName.endsWith(".kml");

    if (!isKmz && !isKml) {
      setKmlError("Please upload a .kml or .kmz file.");
      event.target.value = "";
      return;
    }

    setKmlLoading(true);
    setKmlError("");

    try {
      let kmlText;

      if (isKmz) {
        const zip = await JSZip.loadAsync(file);

        const kmlEntry = Object.values(zip.files).find((entry) =>
          entry.name.toLowerCase().endsWith(".kml"),
        );

        if (!kmlEntry) {
          throw new Error("No KML file found inside this KMZ archive.");
        }

        kmlText = await kmlEntry.async("text");
      } else {
        kmlText = await file.text();
      }

      const parser = new DOMParser();
      const kmlDom = parser.parseFromString(kmlText, "text/xml");

      const parserError = kmlDom.querySelector("parsererror");
      if (parserError) {
        throw new Error("This file does not look like valid KML.");
      }

      const geoJson = kmlToGeoJson(kmlDom);

      if (!geoJson.features || geoJson.features.length === 0) {
        throw new Error("No mappable features were found in this file.");
      }

      const color =
        KML_LAYER_COLORS[kmlLayers.length % KML_LAYER_COLORS.length];

      setKmlLayers((previous) => [
        ...previous,
        {
          id: Date.now(),
          name: file.name,
          geoJson,
          visible: true,
          color,
        },
      ]);

      window.dispatchEvent(
        new CustomEvent("lims-fit-kml", { detail: { geoJson } }),
      );

      const center = getGeoJsonCenter(geoJson);
      if (center) {
        fetchWeatherForLocation({
          ...center,
          label: file.name,
          icon: "kml",
        });
      }
    } catch (error) {
      console.error("KML/KMZ parse error:", error);
      setKmlError(error.message || "Unable to read this file.");
    } finally {
      setKmlLoading(false);
      event.target.value = "";
    }
  };

  const toggleKmlLayer = (id) => {
    setKmlLayers((previous) =>
      previous.map((layer) =>
        layer.id === id ? { ...layer, visible: !layer.visible } : layer,
      ),
    );
  };

  const removeKmlLayer = (id) => {
    setKmlLayers((previous) => previous.filter((layer) => layer.id !== id));
  };

  // ====================================================
  // TOOL BUTTON STYLE
  // ====================================================

  const toolButtonStyle = {
    width: 46,
    height: 46,
    borderRadius: 0,
    color: "#333",
    backgroundColor: "#fff",
    transition: "background-color 0.15s ease, color 0.15s ease",
    "&:hover": {
      backgroundColor: "#f1f8f4",
      color: "#2e7d32",
    },
    "& svg": {
      fontSize: 22,
    },
  };

  const activeToolButtonStyle = {
    ...toolButtonStyle,
    color: "#2e7d32",
    backgroundColor: "#e8f5e9",
    "&:hover": {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
  };

  // ====================================================
  // RETURN
  // ====================================================

  return (
    <Box
      id="lims-map-container"
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        "&:fullscreen": { width: "100vw", height: "100vh" },
      }}
    >
      {/* Hidden file input for KML/KMZ upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".kml,.kmz"
        style={{ display: "none" }}
        onChange={handleKmlUpload}
      />

      {/* =================================================
          MAP
      ================================================= */}

      <MapContainer
        center={PAKISTAN_CENTER}
        zoom={5}
        minZoom={3}
        maxZoom={19}
        zoomControl={false}
        style={{ width: "100%", height: "100%" }}
      >
        <MapController
          searchLocation={searchLocation}
          userLocation={userLocation}
        />

        <MapClickHandler
          labelMode={labelMode}
          setLabels={setLabels}
          setLabelMode={setLabelMode}
          pinMode={pinMode}
          setPins={setPins}
          setPinMode={setPinMode}
          setClickedPosition={setClickedPosition}
          onPinPlaced={handlePinPlaced}
        />

        <MapInteractionController
          featureGroupRef={featureGroupRef}
          setSelectedArea={setSelectedArea}
          onShapeCreated={handleShapeCreated}
        />

        {/* Base layer */}

        {mapType === "satellite" && (
          <TileLayer
            url={SATELLITE_URL}
            attribution="Tiles © Esri"
            maxZoom={19}
          />
        )}

        {mapType === "street" && (
          <TileLayer
            url={STREET_URL}
            attribution="© OpenStreetMap contributors"
            maxZoom={19}
          />
        )}

        {/* Country / province / district reference labels */}

        {showAdminLabels && (
          <TileLayer
            url={ADMIN_LABELS_URL}
            attribution="Labels © Esri"
            maxZoom={19}
            opacity={1}
          />
        )}

        {/* Detailed place labels: cities, towns, villages, localities.
            Sourced from OpenStreetMap place data via CARTO, so coverage
            depth (down to village level) follows however well an area
            is mapped in OSM. */}

        {showPlaceLabels && (
          <TileLayer
            url={PLACE_LABELS_URL}
            attribution="© OpenStreetMap contributors © CARTO"
            maxZoom={19}
            opacity={0.95}
          />
        )}

        <ScaleControl position="bottomleft" imperial={false} />

        {/* Drawing feature group */}

        <FeatureGroup ref={featureGroupRef} />

        {/* =================================================
            UPLOADED KML / KMZ LAYERS
        ================================================= */}

        {kmlLayers.map((layer) =>
          layer.visible ? (
            <GeoJSON
              key={layer.id}
              data={layer.geoJson}
              style={{
                color: layer.color,
                weight: 3,
                fillColor: layer.color,
                fillOpacity: 0.15,
              }}
              onEachFeature={(feature, leafletLayer) => {
                leafletLayer.bindPopup(
                  buildKmlPopupContent(feature, layer.name),
                );
              }}
            />
          ) : null,
        )}

        {/* Search marker */}

        {searchMarker && (
          <Marker position={[searchMarker.latitude, searchMarker.longitude]}>
            <Popup>
              <strong>{searchMarker.name}</strong>
            </Popup>
          </Marker>
        )}

        {/* Current location */}

        {userLocation && (
          <CircleMarker
            center={[userLocation.latitude, userLocation.longitude]}
            radius={9}
            pathOptions={{
              color: "#ffffff",
              weight: 3,
              fillColor: "#1976d2",
              fillOpacity: 1,
            }}
          >
            <Popup>Your current location</Popup>
          </CircleMarker>
        )}

        {/* Custom text labels */}

        {labels.map((label) => (
          <Marker key={label.id} position={[label.latitude, label.longitude]}>
            <LeafletTooltip
              permanent
              direction="top"
              offset={[0, -10]}
              opacity={1}
            >
              {label.text}
            </LeafletTooltip>

            <Popup>
              <Box sx={{ minWidth: 150 }}>
                <Typography fontSize={13} fontWeight={600}>
                  {label.text}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => deleteLabel(label.id)}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  Delete Label
                </Button>
              </Box>
            </Popup>
          </Marker>
        ))}

        {/* User-dropped pins */}

        {pins.map((pin) => (
          <Marker
            key={pin.id}
            position={[pin.latitude, pin.longitude]}
            icon={pinIcon}
            draggable
            eventHandlers={{
              dragend: (event) => {
                const { lat, lng } = event.target.getLatLng();
                setPins((previous) =>
                  previous.map((p) =>
                    p.id === pin.id
                      ? { ...p, latitude: lat, longitude: lng }
                      : p,
                  ),
                );
                fetchWeatherForLocation({
                  latitude: lat,
                  longitude: lng,
                  label: "Dropped Pin",
                  icon: "pin",
                });
              },
            }}
          >
            <Popup>
              <Box sx={{ minWidth: 150 }}>
                <Typography fontSize={13} fontWeight={600}>
                  Pin
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {pin.latitude.toFixed(6)}, {pin.longitude.toFixed(6)}
                </Typography>
                <Typography
                  fontSize={10}
                  color="text.secondary"
                  sx={{ mt: 0.3 }}
                >
                  Drag to reposition
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => deletePin(pin.id)}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  Delete Pin
                </Button>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* =================================================
          SEARCH BAR - LEFT
      ================================================= */}

      <Paper
  elevation={5}
  sx={{
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 2500,
    width: { xs: "calc(100% - 40px)", md: "auto" },
    borderRadius: "10px",
    overflow: "visible",
  }}
>
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "stretch", sm: "center" },
      minHeight: 52,
      backgroundColor: "#fff",
      borderRadius: "10px",
      p: 0.8,
      gap: 1,
    }}
  >
    {/* Input Field Area */}
    <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 240 }}>
      <SearchIcon sx={{ ml: 1, color: "#777" }} />
      <TextField
        fullWidth
        size="small"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onFocus={() => {
          if (search.trim()) setShowSuggestions(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") handleSearch();
          if (event.key === "Escape") setShowSuggestions(false);
        }}
        placeholder="Search ..."
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: { px: 1, height: 40, fontSize: 14 },
        }}
      />
      {loadingSuggestions && <CircularProgress size={18} sx={{ mr: 1 }} />}
    </Box>

    {/* Search Button */}
    <Button
      variant="contained"
      onClick={handleSearch}
      disabled={loadingSearch}
      sx={{
        minWidth: 78,
        height: 40,
        px: 2,
        textTransform: "none",
        borderRadius: "7px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {loadingSearch ? "..." : "Search"}
    </Button>

    {/* Calculate Crop Water Requirement Button */}
    <Button
      variant="contained"
      color="success"
      onClick={handleCalculateCropWater}
      sx={{
        height: 40,
        px: 2, // Adds horizontal padding so text never touches edges
        textTransform: "none",
        borderRadius: "7px",
        whiteSpace: "nowrap",
        fontWeight: 600,
        fontSize: 13,
        flexShrink: 0, // Prevents flexbox from squeezing the button
      }}
    >
      Calculate Crop Water Requirement
    </Button>
  </Box>

  {/* Suggestions Dropdown */}
  {showSuggestions && suggestions.length > 0 && (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        mt: 0.5,
        left: 0,
        right: 0,
        zIndex: 3000,
        backgroundColor: "#fff",
        borderRadius: "10px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
        overflow: "hidden",
        maxHeight: 350,
        overflowY: "auto",
      }}
    >
      {suggestions.map((result, index) => (
        <Box
          key={`${result.place_id}-${index}`}
          onClick={() => selectSuggestion(result)}
          sx={{
            px: 2,
            py: 1.3,
            cursor: "pointer",
            borderBottom:
              index !== suggestions.length - 1
                ? "1px solid #eee"
                : "none",
            "&:hover": { backgroundColor: "#f1f8f4" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
            <LocationOnIcon
              sx={{ fontSize: 20, color: "#2e7d32", mt: 0.2 }}
            />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#222" }}>
                {result.name || result.display_name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#777",
                  mt: 0.3,
                  lineHeight: 1.4,
                }}
              >
                {result.display_name}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )}
</Paper>
        
      {/* =================================================
          KML UPLOAD ERROR TOAST
      ================================================= */}

      {kmlError && (
        <Alert
          severity="error"
          onClose={() => setKmlError("")}
          sx={{
            position: "absolute",
            top: 82,
            left: 20,
            zIndex: 2600,
            width: { xs: "calc(100% - 90px)", sm: 500 },
          }}
        >
          {kmlError}
        </Alert>
      )}

      {/* =================================================
          WEATHER PANEL - LEFT SIDE
          Populated whenever the user drops a pin, finishes a
          polygon / rectangle / circle, or uploads a KML/KMZ file.
      ================================================= */}

      {showWeatherPanel && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: kmlError ? 130 : 82, // unchanged
            left: 20,
            zIndex: 2400,
            width: { xs: "calc(100% - 40px)", sm: 340 },
            maxHeight: "calc(100vh - 100px)", // was 110px, increase a bit
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.3,
              background: "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "#fff",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize={14} fontWeight={700}>
                10‑Day Forecast
              </Typography>

              {weatherSource && (
                <>
                  <Typography
                    fontSize={11.5}
                    sx={{
                      opacity: 0.9,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={weatherSource.label}
                  >
                    {weatherSource.label}
                  </Typography>
                  <Typography fontSize={10.5} sx={{ opacity: 0.75 }}>
                    {weatherSource.latitude.toFixed(4)},{" "}
                    {weatherSource.longitude.toFixed(4)}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 0.3 }}>
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    size="small"
                    disabled={!weatherSource || weatherLoading}
                    onClick={() =>
                      weatherSource && fetchWeatherForLocation(weatherSource)
                    }
                    sx={{ color: "#fff" }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <IconButton
                size="small"
                onClick={closeWeatherPanel}
                sx={{
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ overflowY: "auto", backgroundColor: "#f5f7fa", p: 1.5 }}>
            {weatherLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {!weatherLoading && weatherError && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ fontSize: 12 }}>
                  {weatherError}
                </Alert>
              </Box>
            )}

            {!weatherLoading && !weatherError && weatherDays && (
              <>
                {/* Forecast cards */}
                {weatherDays.map((forecastDay, index) => {
                  // Background tint based on max temp
                  let bg = "#fff";
                  if (forecastDay.tempMax >= 40) bg = "#ffebee";
                  else if (forecastDay.tempMax >= 35) bg = "#fff3e0";
                  else if (forecastDay.tempMax <= 5) bg = "#e3f2fd";

                  return (
                    <Paper
                      key={forecastDay.day}
                      elevation={1}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: bg,
                        transition: "transform 0.15s",
                        "&:hover": { transform: "scale(1.01)" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.2,
                        }}
                      >
                        {/* Weather icon */}
                        {forecastDay.icon ? (
                          <Box
                            component="img"
                            src={`https://openweathermap.org/img/wn/${forecastDay.icon}@2x.png`}
                            alt={forecastDay.description}
                            sx={{ width: 40, height: 40, flexShrink: 0 }}
                          />
                        ) : (
                          <CloudQueueIcon
                            sx={{
                              fontSize: 32,
                              color: "#90a4ae",
                              flexShrink: 0,
                            }}
                          />
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Day + Date */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                            }}
                          >
                            <Typography fontSize={13} fontWeight={700}>
                              {index === 0
                                ? "Today"
                                : forecastDay.date.toLocaleDateString("en-US", {
                                    weekday: "short",
                                  })}
                            </Typography>
                            <Typography fontSize={10.5} color="text.secondary">
                              {forecastDay.date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}
                            </Typography>
                          </Box>

                          {/* Description */}
                          <Typography
                            fontSize={11}
                            color="text.secondary"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {forecastDay.description}
                          </Typography>

                          {/* Temperature & Humidity grid with labels */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(65px, 1fr))",
                              gap: 0.5,
                              mt: 0.8,
                            }}
                          >
                            <Box>
                              <Typography fontSize={9} color="text.secondary">
                                Max
                              </Typography>
                              <Typography
                                fontSize={13}
                                fontWeight={700}
                                color="error.main"
                              >
                                {forecastDay.tempMax}°
                              </Typography>
                            </Box>
                            <Box>
                              <Typography fontSize={9} color="text.secondary">
                                Min
                              </Typography>
                              <Typography
                                fontSize={13}
                                fontWeight={700}
                                color="primary.main"
                              >
                                {forecastDay.tempMin}°
                              </Typography>
                            </Box>
                            <Box>
                              <Typography fontSize={9} color="text.secondary">
                                Day
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.3,
                                }}
                              >
                                <WbSunnyIcon
                                  sx={{ fontSize: 12, color: "#f9a825" }}
                                />
                                <Typography
                                  fontSize={12}
                                  color="text.secondary"
                                >
                                  {forecastDay.tempDay}°
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <Typography fontSize={9} color="text.secondary">
                                Night
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.3,
                                }}
                              >
                                <NightsStayIcon
                                  sx={{ fontSize: 12, color: "#5c6bc0" }}
                                />
                                <Typography
                                  fontSize={12}
                                  color="text.secondary"
                                >
                                  {forecastDay.tempNight}°
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <Typography fontSize={9} color="text.secondary">
                                Humidity
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.3,
                                }}
                              >
                                <WaterDropIcon
                                  sx={{ fontSize: 12, color: "#29b6f6" }}
                                />
                                <Typography
                                  fontSize={12}
                                  color="text.secondary"
                                >
                                  {forecastDay.humidity}%
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* ETo line */}
                          {forecastDay.eto != null && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.8,
                              }}
                            >
                              <WaterDropIcon
                                sx={{ fontSize: 14, color: "#4caf50" }}
                              />
                              <Typography fontSize={11} color="text.secondary">
                                ETo: <strong>{forecastDay.eto} mm</strong>
                              </Typography>
                            </Box>
                          )}

                          {/* Alerts */}
                          {(forecastDay.heatAlert ||
                            forecastDay.frostAlert ||
                            forecastDay.rainAlert ||
                            forecastDay.windAlert) && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                mt: 0.6,
                                flexWrap: "wrap",
                              }}
                            >
                              {forecastDay.heatAlert && (
                                <Box
                                  sx={{
                                    fontSize: 9.5,
                                    px: 0.7,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    backgroundColor: "#ffebee",
                                    color: "#c62828",
                                  }}
                                >
                                  🔥 {forecastDay.heatAlert}
                                </Box>
                              )}
                              {forecastDay.frostAlert && (
                                <Box
                                  sx={{
                                    fontSize: 9.5,
                                    px: 0.7,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    backgroundColor: "#e3f2fd",
                                    color: "#1565c0",
                                  }}
                                >
                                  ❄️ {forecastDay.frostAlert}
                                </Box>
                              )}
                              {forecastDay.rainAlert && (
                                <Box
                                  sx={{
                                    fontSize: 9.5,
                                    px: 0.7,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    backgroundColor: "#e0f7fa",
                                    color: "#00695c",
                                  }}
                                >
                                  🌧️ {forecastDay.rainAlert}
                                </Box>
                              )}
                              {forecastDay.windAlert && (
                                <Box
                                  sx={{
                                    fontSize: 9.5,
                                    px: 0.7,
                                    py: 0.2,
                                    borderRadius: "4px",
                                    backgroundColor: "#f3e5f5",
                                    color: "#6a1b9a",
                                  }}
                                >
                                  💨 {forecastDay.windAlert}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}

                {/* ETo Summary Card */}
                {weatherDays.length > 0 &&
                  (() => {
                    const etos = weatherDays
                      .map((d) => d.eto)
                      .filter((v) => v != null);
                    if (etos.length === 0) return null;
                    const sum = etos.reduce((a, b) => a + b, 0);
                    const avg = sum / etos.length;
                    const max = Math.max(...etos);
                    const min = Math.min(...etos);

                    return (
                      <Paper
                        elevation={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#e8f5e9",
                          border: "1px solid #a5d6a7",
                          mt: 1,
                          mb: 8,

                        }}
                      >
                        <Typography
                          fontSize={12}
                          fontWeight={600}
                          color="green"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <WaterDropIcon fontSize="small" /> ETo Summary
                          (mm/day)
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-around",
                            mt: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box>
                            <Typography fontSize={10} color="text.secondary">
                              Average
                            </Typography>
                            <Typography
                              fontSize={15}
                              fontWeight={700}
                              color="#2e7d32"
                            >
                              {avg.toFixed(1)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography fontSize={10} color="text.secondary">
                              Total (10d)
                            </Typography>
                            <Typography
                              fontSize={15}
                              fontWeight={700}
                              color="#2e7d32"
                            >
                              {sum.toFixed(1)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography fontSize={10} color="text.secondary">
                              Max
                            </Typography>
                            <Typography
                              fontSize={15}
                              fontWeight={700}
                              color="#c62828"
                            >
                              {max.toFixed(1)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography fontSize={10} color="text.secondary">
                              Min
                            </Typography>
                            <Typography
                              fontSize={15}
                              fontWeight={700}
                              color="#1565c0"
                            >
                              {min.toFixed(1)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })()}
              </>
            )}
          </Box>

          
        </Paper>
      )}

      {/* =================================================
          RIGHT TOOLBAR
      ================================================= */}

      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 2500,
          display: "flex",
          flexDirection: "column",
          borderRadius: "7px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <Tooltip title="Draw Polygon" placement="left">
          <IconButton
            onClick={() => activateDrawing("polygon")}
            sx={toolButtonStyle}
          >
            <PentagonIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Draw Rectangle" placement="left">
          <IconButton
            onClick={() => activateDrawing("rectangle")}
            sx={toolButtonStyle}
          >
            <CropFreeIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Draw Circle" placement="left">
          <IconButton
            onClick={() => activateDrawing("circle")}
            sx={toolButtonStyle}
          >
            <RadioButtonUncheckedIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip
          title={pinMode ? "Click map to place pin" : "Add Pin"}
          placement="left"
        >
          <IconButton
            onClick={togglePinMode}
            sx={pinMode ? activeToolButtonStyle : toolButtonStyle}
          >
            <AddLocationAltIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip
          title={labelMode ? "Click map to place label" : "Add Map Label"}
          placement="left"
        >
          <IconButton
            onClick={toggleLabelMode}
            sx={labelMode ? activeToolButtonStyle : toolButtonStyle}
          >
            <LabelIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Upload KML / KMZ" placement="left">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={kmlLoading}
            sx={toolButtonStyle}
          >
            {kmlLoading ? <CircularProgress size={20} /> : <UploadFileIcon />}
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Map Layers" placement="left">
          <IconButton
            onClick={() => setShowLayers((previous) => !previous)}
            sx={toolButtonStyle}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Zoom In" placement="left">
          <IconButton
            onClick={() =>
              window.dispatchEvent(new CustomEvent("lims-zoom-in"))
            }
            sx={toolButtonStyle}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Zoom Out" placement="left">
          <IconButton
            onClick={() =>
              window.dispatchEvent(new CustomEvent("lims-zoom-out"))
            }
            sx={toolButtonStyle}
          >
            <RemoveIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Reset Map" placement="left">
          <IconButton onClick={resetMap} sx={toolButtonStyle}>
            <ExploreIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="My Location" placement="left">
          <IconButton onClick={getCurrentLocation} sx={toolButtonStyle}>
            <MyLocationIcon />
          </IconButton>
        </Tooltip>

        <Divider />

        <Tooltip title="Fullscreen" placement="left">
          <IconButton onClick={toggleFullscreen} sx={toolButtonStyle}>
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* =================================================
          LAYERS PANEL
      ================================================= */}

      {showLayers && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: 20,
            right: 76,
            zIndex: 3000,
            width: 280,
            borderRadius: 2,
            overflow: "hidden",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Typography fontWeight={700} fontSize={15}>
              Map Layers
            </Typography>
          </Box>

          <Divider />

          <Button
            fullWidth
            onClick={() => {
              setMapType("satellite");
              setShowLayers(false);
            }}
            sx={{
              justifyContent: "space-between",
              px: 2,
              py: 1.3,
              textTransform: "none",
              color: "#333",
              background: mapType === "satellite" ? "#e8f5e9" : "#fff",
            }}
          >
            <span>🛰️ Satellite</span>
            {mapType === "satellite" && (
              <CheckIcon fontSize="small" color="success" />
            )}
          </Button>

          <Button
            fullWidth
            onClick={() => {
              setMapType("street");
              setShowLayers(false);
            }}
            sx={{
              justifyContent: "space-between",
              px: 2,
              py: 1.3,
              textTransform: "none",
              color: "#333",
              background: mapType === "street" ? "#e8f5e9" : "#fff",
            }}
          >
            <span>🗺️ Street Map</span>
            {mapType === "street" && (
              <CheckIcon fontSize="small" color="success" />
            )}
          </Button>

          <Divider />

          <Box
            sx={{
              px: 2,
              py: 1.3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography fontSize={13} fontWeight={600}>
                🏷️ Admin Labels
              </Typography>
              <Typography fontSize={10.5} color="text.secondary">
                Country, province, district
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={showAdminLabels}
              onChange={() => setShowAdminLabels((previous) => !previous)}
            />
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography fontSize={13} fontWeight={600}>
                📍 Place Labels
              </Typography>
              <Typography fontSize={10.5} color="text.secondary">
                Cities, towns & villages
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={showPlaceLabels}
              onChange={() => setShowPlaceLabels((previous) => !previous)}
            />
          </Box>

          {kmlLayers.length > 0 && (
            <>
              <Divider />

              <Box sx={{ px: 2, pt: 1.3, pb: 0.5 }}>
                <Typography fontWeight={700} fontSize={13}>
                  Uploaded Files
                </Typography>
              </Box>

              {kmlLayers.map((layer) => (
                <Box
                  key={layer.id}
                  sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <MapOutlinedIcon sx={{ fontSize: 18, color: layer.color }} />

                  <Typography
                    fontSize={12}
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={layer.name}
                  >
                    {layer.name}
                  </Typography>

                  <Switch
                    size="small"
                    checked={layer.visible}
                    onChange={() => toggleKmlLayer(layer.id)}
                  />

                  <IconButton
                    size="small"
                    onClick={() => removeKmlLayer(layer.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}
        </Paper>
      )}

      {/* =================================================
          AREA CARD
      ================================================= */}

      {selectedArea && selectedArea.squareMeters > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: 20,
            right: 80,
            zIndex: 2500,
            p: 2,
            minWidth: 250,
            borderRadius: 2,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography fontSize={12} color="text.secondary">
                Selected Area
              </Typography>
              <Typography
                fontSize={13}
                fontWeight={600}
                sx={{ mt: 0.5, textTransform: "capitalize" }}
              >
                {selectedArea.type}
              </Typography>
              <Typography
                fontSize={26}
                fontWeight={700}
                color="primary"
                sx={{ mt: 0.5 }}
              >
                {selectedArea.acres.toFixed(2)} acres
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                {selectedArea.hectares.toFixed(3)} hectares
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                {selectedArea.squareMeters.toFixed(0)} m²
              </Typography>
            </Box>

            <IconButton size="small" onClick={() => setSelectedArea(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* =================================================
          MODE MESSAGES
      ================================================= */}

      {(labelMode || pinMode) && (
        <Paper
          elevation={5}
          sx={{
            position: "absolute",
            bottom: 55,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2500,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
          }}
        >
          <Typography fontSize={13} fontWeight={600}>
            {pinMode
              ? "Click anywhere on the map to drop a pin"
              : "Click anywhere on the map to place a label"}
          </Typography>
        </Paper>
      )}

      {/* =================================================
          COORDINATES
      ================================================= */}

      {clickedPosition && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            right: 10,
            bottom: 10,
            zIndex: 2500,
            px: 1.5,
            py: 0.8,
            display: "flex",
            gap: 2,
            fontSize: 11,
            borderRadius: 1.5,
          }}
        >
          <Typography fontSize={11}>
            Lat: {clickedPosition.latitude.toFixed(6)}
          </Typography>
          <Typography fontSize={11}>
            Lng: {clickedPosition.longitude.toFixed(6)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
